import React, { useState } from 'react';
import { Modal, Button, Row, Col } from 'antd';
import { UploadOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import moment from 'moment';
import PropTypes from 'prop-types';

moment.locale('en');

const predefinedRanges = {
	Today: [moment().startOf('day'), moment().endOf('day')],
	Yesterday: [
		moment().subtract(1, 'day').startOf('day'),
		moment().subtract(1, 'day').endOf('day'),
	],
	'Last 3 Days': [
		moment().subtract(3, 'day').startOf('day'),
		moment().subtract(1, 'day').endOf('day'),
	],
	'Last Week': [
		moment().subtract(1, 'week').startOf('week'),
		moment().subtract(1, 'week').endOf('week'),
	],
	'Last Month': [
		moment().subtract(1, 'month').startOf('month'),
		moment().subtract(1, 'month').endOf('month'),
	],
	'Last 3 Months': [
		moment().subtract(3, 'month').startOf('month'),
		moment().subtract(1, 'month').endOf('month'),
	],
	'Last Year': [
		moment().subtract(1, 'year').startOf('year'),
		moment().subtract(1, 'year').endOf('year'),
	],
};

const LAST_3_MONTHS = 'Last 3 Months';
const LAST_YEAR = 'Last Year';

function CustomCalendar({ dateRange, setDateRange }) {
	const [currentMonth, setCurrentMonth] = useState(moment().utcOffset(5.5));

	const daysInMonth = currentMonth.daysInMonth();
	const firstDayOfMonth = (currentMonth.clone().startOf('month').utcOffset(5.5).day() + 6) % 7;

	const prevMonth = () => setCurrentMonth(currentMonth.clone().subtract(1, 'month'));
	const nextMonth = () => setCurrentMonth(currentMonth.clone().add(1, 'month'));

	const handleDateClick = (day) => {
		const clickedDate = currentMonth.clone().date(day).utcOffset(5.5);

		if (!dateRange[0] || (dateRange[0] && dateRange[1])) {
			setDateRange([clickedDate.startOf('day'), null]);
		} else if (clickedDate.isBefore(dateRange[0])) {
			setDateRange([clickedDate.startOf('day'), dateRange[0].endOf('day')]);
		} else {
			setDateRange([dateRange[0], clickedDate.endOf('day')]);
		}
	};

	const handleKeyDown = (event, day) => {
		if (event.key === 'Enter' || event.key === ' ') {
			event.preventDefault();
			handleDateClick(day);
		}
	};

	const isInRange = (day) => {
		if (!dateRange[0] || !dateRange[1]) return false;
		const date = currentMonth.clone().date(day).utcOffset(5.5);
		return date.isBetween(dateRange[0], dateRange[1], 'day', '[]');
	};

	const isStartDate = (day) => {
		if (!dateRange[0]) return false;
		const date = currentMonth.clone().date(day).utcOffset(5.5);
		return date.isSame(dateRange[0], 'day');
	};

	const isEndDate = (day) => {
		if (!dateRange[1]) return false;
		const date = currentMonth.clone().date(day).utcOffset(5.5);
		return date.isSame(dateRange[1], 'day');
	};

	const getDayStyle = (isStart, isEnd, isSelected) => {
		let backgroundColor = 'transparent';
		let color = '#333';

		if (isStart || isEnd) {
			backgroundColor = '#1890ff';
			color = 'white';
		} else if (isSelected) {
			backgroundColor = '#e6f7ff';
		}

		return {
			textAlign: 'center',
			padding: '8px',
			cursor: 'pointer',
			borderRadius: '50%',
			width: '24px',
			height: '24px',
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			fontSize: '12px',
			backgroundColor,
			color,
			margin: '0 auto',
			position: 'relative',
		};
	};

	const renderCalendarDays = () => {
		const days = [];
		const weekDays = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

		// Add week day headers
		weekDays.forEach((day) => {
			days.push(
				<div
					key={`header-${day}`}
					style={{
						textAlign: 'center',
						fontWeight: 'bold',
						padding: '8px',
						fontSize: '12px',
						color: '#666',
					}}
				>
					{day}
				</div>
			);
		});

		// Add empty cells for days before the first day of the month
		for (let i = 0; i < firstDayOfMonth; i += 1) {
			days.push(<div key={`empty-${i}`} style={{ padding: '8px' }}></div>);
		}

		// Add days of the month
		for (let day = 1; day <= daysInMonth; day += 1) {
			const isSelected = isInRange(day);
			const isStart = isStartDate(day);
			const isEnd = isEndDate(day);

			days.push(
				<div
					key={day}
					onClick={() => handleDateClick(day)}
					onKeyDown={(event) => handleKeyDown(event, day)}
					role="button"
					tabIndex={0}
					style={getDayStyle(isStart, isEnd, isSelected)}
				>
					{day}
					{isSelected && !isStart && !isEnd && (
						<div
							style={{
								position: 'absolute',
								top: '50%',
								left: '50%',
								transform: 'translate(-50%, -50%)',
								width: '100%',
								height: '100%',
								backgroundColor: '#e6f7ff',
								borderRadius: '50%',
								zIndex: -1,
							}}
						/>
					)}
				</div>
			);
		}

		// Add empty cells for days after the last day of the month
		const remainingCells = 42 - days.length; // 6 rows * 7 days = 42
		for (let i = 0; i < remainingCells; i += 1) {
			days.push(<div key={`empty-end-${i}`} style={{ padding: '8px' }}></div>);
		}

		return days;
	};

	return (
		<div style={{ border: '1px solid #d9d9d9', borderRadius: '6px', padding: '16px' }}>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: '16px',
				}}
			>
				<LeftOutlined onClick={prevMonth} style={{ cursor: 'pointer', fontSize: '12px' }} />
				<span style={{ fontWeight: 'bold', fontSize: '14px' }}>
					{currentMonth.format('MMMM YYYY')}
				</span>
				<RightOutlined
					onClick={nextMonth}
					style={{ cursor: 'pointer', fontSize: '12px' }}
				/>
			</div>

			<div
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(7, 1fr)',
					gap: '2px',
				}}
			>
				{renderCalendarDays()}
			</div>
		</div>
	);
}

CustomCalendar.propTypes = {
	dateRange: PropTypes.array,
	setDateRange: PropTypes.func.isRequired,
};

CustomCalendar.defaultProps = {
	dateRange: [null, null],
};

// eslint-disable-next-line react/no-multi-comp
function ExportCustomCalender({ exportModalVisible, setExportModalVisible, onExport }) {
	const [dateRange, setDateRange] = useState([null, null]);
	const [includeTrips, setIncludeTrips] = useState(false);

	const handlePredefinedRangeClick = (label) => {
		const [start, end] = predefinedRanges[label];
		setDateRange([start, end]);
	};

	const handleReset = () => {
		setDateRange([null, null]);
		setIncludeTrips(false);
	};

	const handleIncludeTripsChange = (checked) => {
		setIncludeTrips(checked);

		// If checking include trips and current selection is Last Year or Last 3 Months, reset
		if (checked && dateRange[0] && dateRange[1]) {
			const isLastYear =
				dateRange[0].isSame(predefinedRanges[LAST_YEAR][0], 'day') &&
				dateRange[1].isSame(predefinedRanges[LAST_YEAR][1], 'day');
			const isLast3Months =
				dateRange[0].isSame(predefinedRanges[LAST_3_MONTHS][0], 'day') &&
				dateRange[1].isSame(predefinedRanges[LAST_3_MONTHS][1], 'day');

			if (isLastYear || isLast3Months) {
				setDateRange([null, null]);
			}
		}
	};

	const handleExport = () => {
		if (!dateRange[0] || !dateRange[1]) {
			// Don't export if no dates selected
			return;
		}

		const [startDate, endDate] = dateRange;

		// Format dates as YYYY-MM-DD
		const fromDate = startDate.clone().format('YYYY-MM-DD');
		const toDate = endDate.clone().format('YYYY-MM-DD');

		onExport(toDate, fromDate, includeTrips);
		setExportModalVisible(false);
	};

	const handleModalClose = () => {
		// Reset dates and include trips when modal is closed
		setDateRange([null, null]);
		setIncludeTrips(false);
		setExportModalVisible(false);
	};

	const isRangeDisabled = (label) => {
		if (!includeTrips) return false;
		return label === LAST_3_MONTHS || label === LAST_YEAR;
	};

	const isRangeSelected = (label) => {
		const [start, end] = predefinedRanges[label];
		return (
			dateRange[0] &&
			dateRange[1] &&
			dateRange[0].isSame(start, 'day') &&
			dateRange[1].isSame(end, 'day')
		);
	};

	const getButtonStyle = (disabled, selected) => {
		let color = '#333';
		let cursor = 'pointer';

		if (disabled) {
			color = '#ccc';
			cursor = 'not-allowed';
		} else if (selected) {
			color = '#1890ff';
		}

		return {
			textAlign: 'left',
			padding: '4px 8px',
			height: 'auto',
			border: 'none',
			background: selected ? '#e6f7ff' : 'transparent',
			fontSize: '14px',
			color,
			cursor,
		};
	};

	return (
		<Modal
			title={
				<div
					style={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: 'center',
						gap: '8px',
						fontSize: '16px',
						fontWeight: 'bold',
					}}
				>
					<UploadOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
					<span>Export invoices</span>
				</div>
			}
			open={exportModalVisible}
			onCancel={handleModalClose}
			footer={[
				<Button key="cancel" onClick={handleModalClose}>
					Cancel
				</Button>,
				<Button
					key="export"
					type="primary"
					onClick={handleExport}
					disabled={!dateRange[0] || !dateRange[1]}
				>
					Export
				</Button>,
			]}
			width={500}
			centered
		>
			<div style={{ marginBottom: 20 }}>
				<label>
					<input
						type="checkbox"
						checked={includeTrips}
						onChange={(e) => handleIncludeTripsChange(e.target.checked)}
					/>{' '}
					Include Trips in the Invoices
				</label>
			</div>

			<Row gutter={24}>
				<Col span={8}>
					<div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
						{Object.keys(predefinedRanges).map((label) => {
							const disabled = isRangeDisabled(label);
							const selected = isRangeSelected(label);

							return (
								<Button
									key={label}
									size="small"
									type="text"
									disabled={disabled}
									style={getButtonStyle(disabled, selected)}
									onClick={() => !disabled && handlePredefinedRangeClick(label)}
								>
									{label}
								</Button>
							);
						})}
						<Button
							type="link"
							size="small"
							style={{
								textAlign: 'left',
								padding: '4px 8px',
								color: '#1890ff',
								border: 'none',
								background: 'transparent',
								fontSize: '14px',
							}}
							onClick={handleReset}
						>
							Reset
						</Button>
					</div>
				</Col>

				<Col span={16}>
					<CustomCalendar dateRange={dateRange} setDateRange={setDateRange} />
				</Col>
			</Row>
		</Modal>
	);
}

ExportCustomCalender.propTypes = {
	exportModalVisible: PropTypes.bool.isRequired,
	setExportModalVisible: PropTypes.func.isRequired,
	onExport: PropTypes.func.isRequired,
};

export default ExportCustomCalender;
